import { credentials } from './gmb_api';
import { useCallback } from 'react';
import { useEffect }   from 'react';
import { useRef }      from 'react';
import useScript       from './use_script';


// https://developers.google.com/identity/sign-in/web/reference

export default function GoogleAuthWeb({ state, updateState, requireReauthorization }) {
  const scriptUrl                     = 'https://apis.google.com/js/platform.js';
  const [ scriptLoaded, scriptError ] = useScript(scriptUrl);
  const mounted                       = useRef();

  // effect to track mounted state so our listener is rendered inert when we dismount
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const initializeClient = useCallback(() => {
    const { gapi } = window;

    gapi.load('auth2', async() => {
      /* eslint-disable camelcase */
      await window.gapi.auth2.init({
        client_id:    credentials.clientID,
        scope:        credentials.scope,
        redirect_uri: 'https://webapp.broadly.com'
      });
      /* eslint-enable camelcase */

      const client = gapi.auth2.getAuthInstance();

      if (requireReauthorization)
        await client.signOut();

      // listen for signedIn state changes
      client.isSignedIn.listen(() => {
        if (mounted.current) {
          const accessToken = getAccessToken(client);
          const isSignedIn  = client.isSignedIn.get();
          updateState({ accessToken, isSignedIn });
        }
      });

      const accessToken = getAccessToken(client);
      const isSignedIn  = client.isSignedIn.get();

      updateState({ client, accessToken, isSignedIn });
    });
  }, [ requireReauthorization, updateState ]);

  // once the script is loaded, initialize the client. if the script fails, set
  // error state
  useEffect(() => {
    if (!state.client && mounted.current) {
      if (scriptError)
        updateState({ error: true });
      else if (scriptLoaded)
        initializeClient();
    }
    return () => {};
  }, [ initializeClient, scriptError, scriptLoaded, state.client, updateState ]);

  return null;
}

function getAccessToken(client) {
  if (!client)
    return null;

  const user = client.currentUser.get();
  if (!user)
    return null;

  const accessToken = user.getAuthResponse().access_token;
  return accessToken;
}
